// Some passkey browser extensions (notably LastPass as of 2026-05) return
// Uint8Array for fields the WebAuthn spec requires to be ArrayBuffer (rawId,
// clientDataJSON, attestationObject, authenticatorData, signature,
// userHandle). The Auth0 ACUL SDK validates `instanceof ArrayBuffer` strictly
// and throws `TypeError: Expected an ArrayBuffer`. Bitwarden fixed the same
// bug on their side in bitwarden/clients#13653; LastPass has not.
//
// We wrap navigator.credentials.create/.get and return a proxy
// PublicKeyCredential whose ArrayBuffer-typed fields are guaranteed to be
// actual ArrayBuffers. No-op for spec-compliant authenticators.

type BufferLike = ArrayBuffer | ArrayBufferView | null | undefined;

function toArrayBuffer(value: BufferLike): ArrayBuffer | null {
  if (value == null) return value ?? null;
  if (value instanceof ArrayBuffer) return value;
  if (ArrayBuffer.isView(value)) {
    const view = value as ArrayBufferView;
    return view.buffer.slice(
      view.byteOffset,
      view.byteOffset + view.byteLength
    );
  }
  return value as unknown as ArrayBuffer;
}

function needsNormalization(value: BufferLike): boolean {
  return value != null && !(value instanceof ArrayBuffer);
}

function wrapAttestationResponse(
  response: AuthenticatorAttestationResponse
): AuthenticatorAttestationResponse {
  const clientDataJSON = toArrayBuffer(response.clientDataJSON) as ArrayBuffer;
  const attestationObject = toArrayBuffer(
    response.attestationObject
  ) as ArrayBuffer;

  if (
    clientDataJSON === response.clientDataJSON &&
    attestationObject === response.attestationObject
  ) {
    return response;
  }

  const wrapped = Object.create(
    AuthenticatorAttestationResponse.prototype
  ) as AuthenticatorAttestationResponse;
  Object.defineProperties(wrapped, {
    clientDataJSON: { value: clientDataJSON, enumerable: true },
    attestationObject: { value: attestationObject, enumerable: true },
    getAuthenticatorData: {
      value: response.getAuthenticatorData?.bind(response),
    },
    getPublicKey: { value: response.getPublicKey?.bind(response) },
    getPublicKeyAlgorithm: {
      value: response.getPublicKeyAlgorithm?.bind(response),
    },
    getTransports: { value: response.getTransports?.bind(response) },
  });
  return wrapped;
}

function wrapAssertionResponse(
  response: AuthenticatorAssertionResponse
): AuthenticatorAssertionResponse {
  const clientDataJSON = toArrayBuffer(response.clientDataJSON) as ArrayBuffer;
  const authenticatorData = toArrayBuffer(
    response.authenticatorData
  ) as ArrayBuffer;
  const signature = toArrayBuffer(response.signature) as ArrayBuffer;
  const userHandle = toArrayBuffer(response.userHandle) as ArrayBuffer | null;

  if (
    clientDataJSON === response.clientDataJSON &&
    authenticatorData === response.authenticatorData &&
    signature === response.signature &&
    userHandle === response.userHandle
  ) {
    return response;
  }

  const wrapped = Object.create(
    AuthenticatorAssertionResponse.prototype
  ) as AuthenticatorAssertionResponse;
  Object.defineProperties(wrapped, {
    clientDataJSON: { value: clientDataJSON, enumerable: true },
    authenticatorData: { value: authenticatorData, enumerable: true },
    signature: { value: signature, enumerable: true },
    userHandle: { value: userHandle, enumerable: true },
  });
  return wrapped;
}

function wrapPublicKeyCredential(
  credential: PublicKeyCredential
): PublicKeyCredential {
  const rawId = toArrayBuffer(credential.rawId) as ArrayBuffer;
  const response = credential.response;

  let wrappedResponse: AuthenticatorResponse;
  if (response instanceof AuthenticatorAttestationResponse) {
    wrappedResponse = wrapAttestationResponse(response);
  } else if (response instanceof AuthenticatorAssertionResponse) {
    wrappedResponse = wrapAssertionResponse(response);
  } else {
    wrappedResponse = response;
  }

  if (rawId === credential.rawId && wrappedResponse === response) {
    return credential;
  }

  const wrapped = Object.create(
    PublicKeyCredential.prototype
  ) as PublicKeyCredential;
  Object.defineProperties(wrapped, {
    id: { value: credential.id, enumerable: true },
    type: { value: credential.type, enumerable: true },
    rawId: { value: rawId, enumerable: true },
    response: { value: wrappedResponse, enumerable: true },
    authenticatorAttachment: {
      value: credential.authenticatorAttachment,
      enumerable: true,
    },
    getClientExtensionResults: {
      value: () => credential.getClientExtensionResults(),
    },
    toJSON: {
      value: (credential as unknown as { toJSON?: () => unknown }).toJSON?.bind(
        credential
      ),
    },
  });
  return wrapped;
}

function shouldWrap(credential: Credential | null): boolean {
  if (!credential || credential.type !== "public-key") return false;
  const pkc = credential as PublicKeyCredential;
  if (needsNormalization(pkc.rawId)) return true;
  const response = pkc.response;
  if (response instanceof AuthenticatorAttestationResponse) {
    return (
      needsNormalization(response.clientDataJSON) ||
      needsNormalization(response.attestationObject)
    );
  }
  if (response instanceof AuthenticatorAssertionResponse) {
    return (
      needsNormalization(response.clientDataJSON) ||
      needsNormalization(response.authenticatorData) ||
      needsNormalization(response.signature) ||
      needsNormalization(response.userHandle)
    );
  }
  return false;
}

const PATCH_FLAG = "__htlkgWebAuthnPatched";

export function installWebAuthnArrayBufferShim(): void {
  if (typeof navigator === "undefined" || !navigator.credentials) return;
  const creds = navigator.credentials as unknown as Record<string, unknown>;
  if (creds[PATCH_FLAG]) return;

  const originalCreate = navigator.credentials.create.bind(
    navigator.credentials
  );
  const originalGet = navigator.credentials.get.bind(navigator.credentials);

  navigator.credentials.create = async function patchedCreate(options) {
    const credential = await originalCreate(options);
    return shouldWrap(credential)
      ? wrapPublicKeyCredential(credential as PublicKeyCredential)
      : credential;
  };

  navigator.credentials.get = async function patchedGet(options) {
    const credential = await originalGet(options);
    return shouldWrap(credential)
      ? wrapPublicKeyCredential(credential as PublicKeyCredential)
      : credential;
  };

  creds[PATCH_FLAG] = true;
}
